import { useCallback, useEffect, useMemo, useState } from 'react';
import { BellRing, CalendarPlus, Eye, Search, Trash2, X } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../ui/button';
import { Card } from '../../ui/card';
import { Input } from '../../ui/input';
import { Progress } from '../../ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Skeleton } from '../../ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../ui/table';
import { DeadlineText, StageBadge } from '../QualityBadges';
import { formatDate, isOpen, REVIEW_CYCLE_LABELS, scoreTone } from '../qualityConfig';
import { ActivityCell } from './TaskActivity';
import { NudgeButton } from './NudgeButton';
import { PIPELINE_STAGES, stageTest, TrackingPipeline } from './TrackingPipeline';

const OwnTag = () => <span className="ml-1.5 rounded-full bg-violet-500/15 px-1.5 py-0.5 text-[10px] font-semibold text-violet-700 dark:text-violet-300">You</span>;

const TaskRow = ({ t, isOwn, onView, onExtend, onDelete, onNudge }) => (
    <TableRow className="cursor-pointer" onClick={() => onView(t)} data-testid={`task-row-${t.id}`}>
        <TableCell><p className="font-medium">{t.user_name}{isOwn && <OwnTag />}</p><p className="text-xs text-muted-foreground">{t.user_department || t.user_email}</p></TableCell>
        <TableCell><p className="max-w-[220px] truncate font-medium">{t.title}</p><p className="text-xs text-muted-foreground">{REVIEW_CYCLE_LABELS[t.review_cycle]} · by {t.assigned_by_name}</p></TableCell>
        <TableCell><ActivityCell t={t} /></TableCell>
        <TableCell className="min-w-[130px]">
            <div className="flex items-center gap-2"><Progress value={(t.completed_count / t.total_count) * 100} className="h-1.5 w-16" />
                <span className="font-mono text-xs tabular-nums">{t.completed_count}/{t.total_count}</span></div>
        </TableCell>
        <TableCell className="whitespace-nowrap"><p className="text-sm">{formatDate(t.deadline)}</p><DeadlineText task={t} /></TableCell>
        <TableCell><StageBadge stage={t.stage} dueSoon={t.due_soon} testId={`task-row-status-${t.id}`} /></TableCell>
        <TableCell className={`font-heading text-base font-bold tabular-nums ${scoreTone(t.score ?? t.score_so_far)}`} data-testid={`task-row-score-${t.id}`}>{t.score ?? t.score_so_far ?? '—'}</TableCell>
        <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-end gap-1">
                <Button size="icon" variant="ghost" onClick={() => onView(t)} aria-label="View" data-testid={`task-view-${t.id}`}><Eye className="h-4 w-4" /></Button>
                <NudgeButton task={t} isOwn={isOwn} onNudge={onNudge} />
                {!isOwn && (isOpen(t) || t.status === 'missed') && (
                    <Button size="icon" variant="ghost" onClick={() => onExtend(t)} aria-label="Extend deadline" data-testid={`task-extend-${t.id}`}><CalendarPlus className="h-4 w-4 text-violet-600" /></Button>
                )}
                {!isOwn && <Button size="icon" variant="ghost" onClick={() => onDelete(t)} aria-label="Delete" data-testid={`task-delete-${t.id}`}><Trash2 className="h-4 w-4 text-rose-500" /></Button>}
            </div>
        </TableCell>
    </TableRow>
);

const Filters = ({ query, setQuery, userId, setUserId, users }) => (
    <div className="flex flex-wrap gap-3">
        <div className="relative min-w-[220px] flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search tasks or recruiters..." className="pl-9" data-testid="tasks-search" />
        </div>
        <Select value={userId} onValueChange={setUserId}>
            <SelectTrigger className="w-full sm:w-52" data-testid="tasks-user-filter"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">All recruiters</SelectItem>{users.map(([id, name]) => <SelectItem key={id} value={id}>{name}</SelectItem>)}</SelectContent>
        </Select>
    </div>
);

export const TasksTab = ({ api, refreshKey, currentUserId, onView, onExtend, onDelete, onNudge, onNudgeAll }) => {
    const [tasks, setTasks] = useState(null);
    const [stage, setStage] = useState('all');
    const [query, setQuery] = useState('');
    const [userId, setUserId] = useState('all');

    const load = useCallback(() => api.get('/quality-tasks/admin/tasks').then(({ data }) => setTasks(data.tasks)).catch(() => toast.error('Could not load tasks')), [api]);
    useEffect(() => { load(); }, [load, refreshKey]);

    const users = useMemo(() => [...new Map((tasks || []).map((t) => [t.user_id, t.user_name])).entries()].sort((a, b) => a[1].localeCompare(b[1])), [tasks]);
    const base = useMemo(() => (tasks || []).filter((t) => (userId === 'all' || t.user_id === userId)
        && (!query.trim() || `${t.title} ${t.user_name}`.toLowerCase().includes(query.trim().toLowerCase()))), [tasks, userId, query]);
    const rows = base.filter(stageTest(stage));
    const nudgeable = rows.filter((t) => t.can_nudge && t.user_id !== currentUserId);
    const stageLabel = PIPELINE_STAGES.find((s) => s.id === stage)?.label;

    return (
        <div className="space-y-4" data-testid="quality-tasks-tab">
            <TrackingPipeline tasks={base} value={stage} onChange={setStage} />
            <Card className="overflow-hidden">
                <div className="space-y-3 border-b p-4">
                    <Filters query={query} setQuery={setQuery} userId={userId} setUserId={setUserId} users={users} />
                    <div className="flex flex-wrap items-center justify-between gap-2">
                        {stage !== 'all' ? (
                            <button type="button" onClick={() => setStage('all')} data-testid="tasks-clear-stage"
                                className="inline-flex items-center gap-1.5 rounded-full border border-violet-500 bg-violet-600 px-3 py-1 text-xs font-medium text-white transition-colors hover:bg-violet-700">
                                Showing: {stageLabel} · {rows.length} <X className="h-3 w-3" />
                            </button>
                        ) : <span className="text-xs text-muted-foreground">{rows.length} task{rows.length === 1 ? '' : 's'}</span>}
                        <Button size="sm" variant="outline" disabled={!nudgeable.length} onClick={() => onNudgeAll(nudgeable)}
                            className="border-amber-300 text-amber-700 hover:bg-amber-50 dark:border-amber-800/60 dark:text-amber-300 dark:hover:bg-amber-950/40" data-testid="tasks-nudge-all">
                            <BellRing className="mr-1.5 h-3.5 w-3.5" /> Nudge all{nudgeable.length ? ` (${nudgeable.length})` : ''}
                        </Button>
                    </div>
                </div>
                {!tasks ? <div className="space-y-2 p-4">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-12 w-full" />)}</div> : (
                    <Table>
                        <TableHeader><TableRow>
                            <TableHead>Recruiter</TableHead><TableHead>Task</TableHead><TableHead>Recruiter activity</TableHead><TableHead>Progress</TableHead>
                            <TableHead>Deadline</TableHead><TableHead>Status</TableHead><TableHead>Score</TableHead><TableHead className="text-right">Actions</TableHead>
                        </TableRow></TableHeader>
                        <TableBody>
                            {rows.map((t) => <TaskRow key={t.id} t={t} isOwn={t.user_id === currentUserId} onView={onView} onExtend={onExtend} onDelete={onDelete} onNudge={onNudge} />)}
                            {rows.length === 0 && <TableRow><TableCell colSpan={8} className="py-10 text-center text-muted-foreground" data-testid="tasks-empty">No tasks here yet</TableCell></TableRow>}
                        </TableBody>
                    </Table>
                )}
            </Card>
        </div>
    );
};
